import { useMemo, useRef } from 'react'

import { matchPath, useLocation } from 'react-router'

import { PageFields } from '@tribeplatform/gql-client/graphql'
import { Page, Space } from '@tribeplatform/gql-client/types'
import {
  getCachedSpace,
  useNetwork,
  usePage,
} from '@tribeplatform/react-sdk/hooks'
import { useQueryClient, UseQueryOptions } from '@tribeplatform/react-sdk/lib'
import { decodeURIPath } from '@tribeplatform/react-sdk/utils'

const matchSpacePath = (space: Partial<Space>, path: string) =>
  matchPath(path, {
    path: `${space?.address?.path}`,
    exact: space?.address?.exact,
    strict: false,
  })

export const useCurrentPage = (options?: {
  fields?: PageFields
  useQueryOptions?: UseQueryOptions<Page>
}) => {
  const previousPage = useRef<Page>()
  const queryClient = useQueryClient()
  const { data: network } = useNetwork()
  const { systemSpaces } = network || {}
  const location = useLocation()
  let path
  try {
    path = decodeURIPath(location.pathname)
    // eslint-disable-next-line no-empty
  } catch (e) {}

  const cachedPage = useMemo(() => {
    let space: Space
    space = systemSpaces?.find(s => matchSpacePath(s, path))
    if (space) {
      return { space } as Page
    }

    space = getCachedSpace({ path }, queryClient)
    if (space) {
      return { space } as Page
    }
  }, [systemSpaces, path, queryClient])

  const query = usePage({
    variables: { path },
    fields: options?.fields,
    useQueryOptions: {
      enabled: typeof path === 'string',
      placeholderData: cachedPage,
      notifyOnChangeProps: ['data', 'error'],
      onSuccess: data => {
        previousPage.current = data
      },
      ...options?.useQueryOptions,
    },
  })

  /**
   * useQueryOptions.keepPreviousData is not efficient enough for this use-case (where some path might return the same space/page)
   * The issue is the react-query ignores the placeholder data we provide from the cache.
   * And also we do not want/need to show the loading state
   * therefore, we show the previous space while fetching the next page
   *  */
  const data: Page = query.data || cachedPage || previousPage.current
  return { ...query, data }
}
